import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  X,
  Brain,
  AlertTriangle,
  Activity,
  Zap
} from 'lucide-react';
import apiService from '../services/apiService';

const AIAnalysisModal = ({ isOpen, onClose, dailyRecap }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchDetailedAnalysis();
    }
  }, [isOpen]);

  const fetchDetailedAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate comprehensive AI analysis using our Claude integration
      const response = await apiService.chatWithAI(
        "Provide a comprehensive market analysis report including: 1) Current market overview and sentiment, 2) Top investment opportunities across crypto/equities/commodities, 3) Major risks to watch, 4) Technical analysis summary, 5) Key recommendations for retail investors. Format as a structured report.",
        null // No session ID needed for one-off analysis
      );
      
      // Parse the AI response into structured data
      const analysis = parseAIAnalysis(response.response);
      setAnalysisData(analysis);
      
    } catch (err) {
      console.error('Error fetching detailed analysis:', err);
      setError('Failed to generate AI analysis');
      // Set fallback analysis data
      setAnalysisData(getFallbackAnalysis());
    } finally {
      setLoading(false);
    }
  };

  const parseAIAnalysis = (aiResponse) => {
    // Display the full AI response in the overview, with daily recap data for context cards
    return {
      overview: {
        sentiment: dailyRecap?.market_sentiment?.label || "N/A",
        score: dailyRecap?.market_sentiment?.score || 0,
        summary: aiResponse || "Analysis unavailable. Please try again."
      }
    };
  };

  const getFallbackAnalysis = () => {
    return {
      overview: {
        sentiment: "N/A",
        score: 0,
        summary: "AI analysis is temporarily unavailable. Please try again in a moment."
      }
    };
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish': return 'text-green-600 bg-green-100';
      case 'bearish': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI Market Analysis</h2>
              <p className="text-sm text-gray-600">Comprehensive insights powered by AI</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} className="border-gray-300">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-100px)]">
          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Activity className="w-12 h-12 text-[#F7931A] animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">Generating AI Analysis</h3>
                  <p className="text-gray-500">Claude is analyzing current market conditions...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700">Analysis Unavailable</h3>
                  <p className="text-gray-500 mb-4">{error}</p>
                  <Button onClick={fetchDetailedAnalysis} className="bg-[#F7931A] hover:bg-orange-600">
                    Retry Analysis
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Market Overview */}
                {analysisData && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Market Overview</h3>
                      <Badge className={`${getSentimentColor(analysisData.overview.sentiment)} border-0`}>
                        {analysisData.overview.sentiment} ({analysisData.overview.score}/100)
                      </Badge>
                    </div>
                    
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-gray-700 leading-relaxed">
                          {analysisData.overview.summary}
                        </p>
                      </CardContent>
                    </Card>

                    {dailyRecap && (
                      <div className="grid grid-cols-3 gap-4">
                        <Card>
                          <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-gray-900">{dailyRecap.top_performer?.symbol}</div>
                            <div className="text-sm text-gray-500">Top Performer</div>
                            <div className="text-green-600 font-semibold">
                              +{dailyRecap.top_performer?.change}%
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card>
                          <CardContent className="p-4 text-center">
                            <div className="text-2xl font-bold text-gray-900">
                              {dailyRecap.market_sentiment?.score}
                            </div>
                            <div className="text-sm text-gray-500">Sentiment Score</div>
                            <div className="text-[#F7931A] font-semibold">
                              {dailyRecap.market_sentiment?.label}
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card>
                          <CardContent className="p-4 text-center">
                            <div className="text-lg font-bold text-gray-900">
                              {dailyRecap.key_event?.title}
                            </div>
                            <div className="text-sm text-gray-500">Key Event</div>
                            <div className="text-gray-600 text-xs">
                              {dailyRecap.key_event?.description}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                )}

                {/* Disclaimer shown on all tabs */}
                <Card className="bg-orange-50 border-orange-200 mt-4">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Zap className="w-5 h-5 text-[#F7931A]" />
                      <span className="font-semibold text-orange-800">Disclaimer</span>
                    </div>
                    <p className="text-orange-700 text-sm">
                      This analysis is generated by AI and should not be considered as financial advice.
                      Always conduct your own research and consult with a qualified financial advisor before making investment decisions.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisModal;